import { useCallback, useContext } from 'react';
import { apiService, LoginRequest, RegisterRequest, SocialLoginRequest, MobileLoginRequest } from '../services/api';
import { DevicesContext } from '../contexts/DevicesContext';
import { aqiStandardApi } from '../services/aqiStandardApi';
import { AuthContext } from '../contexts/AuthContext';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  const { authState, setAuthState } = context;

  const devicesContext = useContext(DevicesContext);

  // Login function (Kaiterra-style)
  const login = useCallback(async (credentials: LoginRequest) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await apiService.login(credentials);

      if (response.success && response.data) {
        // Get user from token or response
        const { userId, username, email, role, profilePictureUrl, timezone, loginType, hasPassword } = response.data.user || {};
        const user = { userId, username, email, role, profilePictureUrl, timezone, loginType, hasPassword };

        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });

        localStorage.setItem('userData', JSON.stringify(user));

        const userPreferences = await aqiStandardApi.getUserAccountPreferences();
        if (userPreferences) {
          // Set preferences in localStorage
          localStorage.setItem('userPreferences', JSON.stringify({
            aqiStandardId: userPreferences?.aqiStandardId || null,
            timezone: userPreferences?.timezone || null,
            parametersWithPreferredUnit: userPreferences?.parametersWithPreferredUnit || [],
          }));
        }
        return { success: true };
      } else {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: response.error || 'Login failed'
        }));
        return { success: false, error: response.error };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return { success: false, error: errorMessage };
    }
  }, []);

  // Register function (Kaiterra-style)
  const register = useCallback(async (userData: RegisterRequest) => {
    // setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await apiService.register(userData);

      if (response.success && response.data) {
        // Get user from token or response
        // const user = response.data.user || null;

        // setAuthState({
        //   user,
        //   isAuthenticated: true,
        //   isLoading: false,
        //   error: null
        // });

        return { success: true };
      } else {
        // setAuthState(prev => ({
        //   ...prev,
        //   isLoading: false,
        //   error: response.error || 'Registration failed'
        // }));
        return { success: false, error: response.error };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      // setAuthState(prev => ({
      //   ...prev,
      //   isLoading: false,
      //   error: errorMessage
      // }));
      return { success: false, error: errorMessage };
    }
  }, []);

  // Logout function (Kaiterra-style)
  const logout = useCallback(async () => {
    setAuthState(prev => ({ ...prev, isLoading: true }));

    try {
      // Call logout API
      await apiService.logout();

      devicesContext?.resetState();
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });

    } catch (error) {
      // Even if logout API fails, clear local state
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });

      devicesContext?.resetState();

    }
  }, []);

  // Forgot password function
  const forgotPassword = useCallback(async (email: string) => {
    try {
      const response = await apiService.forgotPassword(email);
      return response;
    } catch (error) {
      return {
        success: false,
        error: 'Failed to send password reset email'
      };
    }
  }, []);

  // Social media login function (Kaiterra-style)
  const socialLogin = useCallback(async (socialData: SocialLoginRequest) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await apiService.socialLogin(socialData);

      if (response.success && response.data) {
        // Get user from token or response
        const { userId, username, email, role, profilePictureUrl, timezone, aqiStandardId } = response.data.user || {};
        const user = { userId, username, email, role, profilePictureUrl, timezone, aqiStandardId };

        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });

        localStorage.setItem('userData', JSON.stringify(user));

        // Get user account preferences and save to localStorage
        const userPreferences = await aqiStandardApi.getUserAccountPreferences();
        if (userPreferences) {
          localStorage.setItem('userPreferences', JSON.stringify(userPreferences));
        }

        return { success: true, message: response.message };
      } else {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: response.error || 'Social login failed'
        }));
        return { success: false, error: response.error || 'Social login failed' };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Social login failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return { success: false, error: errorMessage };
    }
  }, []);

  // Mobile authentication functions
  const sendMobileOTP = useCallback(async (mobileNumber: string) => {
    try {
      const response = await apiService.sendMobileOTP(mobileNumber);
      return response;
    } catch (error) {
      return {
        success: false,
        error: 'Failed to send OTP'
      };
    }
  }, []);

  const verifyMobileOTP = useCallback(async (mobileData: MobileLoginRequest) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await apiService.verifyMobileOTP(mobileData);

      if (response.success && response.data) {
        // Get user from token or response
        const user = response.data.user || null;

        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });

        return { success: true };
      } else {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: response.error || 'Mobile verification failed'
        }));
        return { success: false, error: response.error };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Mobile verification failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return { success: false, error: errorMessage };
    }
  }, []);

  // Reset password function
  const resetPassword = useCallback(async (token: string, newPassword: string, confirmPassword: string) => {
    try {
      const response = await apiService.resetPassword(token, newPassword, confirmPassword);
      return response;
    } catch (error) {
      return {
        success: false,
        error: 'Failed to reset password'
      };
    }
  }, []);

  // Refresh token function (Kaiterra-style)
  const refreshToken = useCallback(async () => {
    try {
      const response = await apiService.refreshAccessToken();

      if (response.success) {
        // Update user state if needed
        return response;
      } else {
        // If refresh fails, logout user
        await logout();
        return { success: false, error: 'Refresh token failed' };
      }
    } catch (error) {
      // If refresh fails, logout user
      await logout();
      return { success: false, error: 'Refresh token failed' };
    }
  }, [logout]);

  // Clear error function
  const clearError = useCallback(() => {
    setAuthState(prev => ({ ...prev, error: null }));
  }, []);

  // Check authentication status
  // const checkAuthStatus = useCallback(() => {
  //   const hasValidTokens = JWTManager.hasValidTokens();
  //   // const currentUser = apiService.getCurrentUser();

  //   setAuthState(prev => ({
  //     ...prev,
  //     isAuthenticated: hasValidTokens,
  //     user: hasValidTokens ? currentUser : null
  //   }));

  //   return hasValidTokens;
  // }, []);

  // Synchronous, no-API-call reset of the auth context. Use when the server
  // has already invalidated the session out-of-band (e.g. after successful
  // email-change confirmation, where token_version was bumped and cookies
  // were expired in the response) and we just need the React state to
  // reflect "signed out" immediately — before navigating away, so that
  // LoginPage's isAuthenticated check doesn't bounce the user to /devices
  // for a brief moment.
  const clearAuthState = useCallback(() => {
    localStorage.removeItem('userData');
    localStorage.removeItem('userPreferences');
    devicesContext?.resetState();
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  // Check is authenticated true or not
  const checkIsAuthenticated = useCallback(async () => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    const response = await apiService.checkIsAuthenticated();
    if (response.success) {
      const { userId, username, email, role, profilePictureUrl, timezone, loginType, hasPassword } = response.data.user || {};
      const user = { userId, username, email, role, profilePictureUrl, timezone, loginType, hasPassword };

      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });

      return { success: true, message: response.message };
    } else {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: response.error || 'Authentication failed'
      }));
      return { success: false, error: response.error };
    }
  }, []);

  return {
    // State
    user: authState.user,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    error: authState.error,

    // Actions
    login,
    register,
    socialLogin,
    sendMobileOTP,
    verifyMobileOTP,
    logout,
    forgotPassword,
    resetPassword,
    refreshToken,
    clearError,
    checkIsAuthenticated,
    clearAuthState
  };
}; 